import { DateTime } from 'luxon';

import { APP_DISPLAY_TIME_ZONE } from '../constants/appTimezone';

function torontoCalendarDateToJs(y: number, month: number, day: number): Date | null {
  const dt = DateTime.fromObject({ year: y, month, day }, { zone: APP_DISPLAY_TIME_ZONE }).startOf('day');
  return dt.isValid ? dt.toJSDate() : null;
}

/**
 * Formate une date pour l'affichage "Dernière analyse AI pour LEX"
 * Format: "2 février 2026 à 08 h 30" (heure du Québec)
 */
export function formatLastAnalysisDate(dateString: string): string {
  const dt = DateTime.fromISO(dateString, { setZone: true });
  if (!dt.isValid) return '';
  const z = dt.setZone(APP_DISPLAY_TIME_ZONE);

  const monthNames = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
  ];
  const month = monthNames[z.month - 1];
  const hours = z.hour.toString().padStart(2, '0');
  const minutes = z.minute.toString().padStart(2, '0');

  return `${z.day} ${month} ${z.year} à ${hours} h ${minutes}`;
}

/**
 * Parse une valeur « jour de calendrier » (réunion, échéance date-only, etc.) pour affichage au Québec.
 * - `YYYY-MM-DD` seul : ce jour à Toronto (pas interprété en UTC).
 * - `YYYY-MM-DDT00:00:00` + `Z` ou `±00:00` : minuit UTC « date seule » (DATE PostgreSQL en JSON) → on garde le Y-M-D du texte.
 * - `YYYY-MM-DDT00:00:00` sans fuseau (certaines sérialisations) → même traitement.
 * - Autres ISO : instant réel (Luxon).
 */
export function parseDateOnlyLocal(dateString: string | null | undefined): Date | null {
  if (dateString == null || dateString === '') return null;
  const s = String(dateString).trim();

  const ymdOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (ymdOnly) {
    return torontoCalendarDateToJs(Number(ymdOnly[1]), Number(ymdOnly[2]), Number(ymdOnly[3]));
  }

  const utcMidnight = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(\.\d+)?(Z|[+-]00:?00)$/i.exec(s);
  if (utcMidnight) {
    return torontoCalendarDateToJs(Number(utcMidnight[1]), Number(utcMidnight[2]), Number(utcMidnight[3]));
  }

  const bareMidnight = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(\.\d+)?$/i.exec(s);
  if (bareMidnight) {
    return torontoCalendarDateToJs(Number(bareMidnight[1]), Number(bareMidnight[2]), Number(bareMidnight[3]));
  }

  const dtLuxon = DateTime.fromISO(s, { setZone: true });
  if (dtLuxon.isValid) return dtLuxon.toJSDate();

  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Format une date "date-only" (YYYY-MM-DD) ou une ISO complète de manière fiable.
 */
export function formatDateLocal(
  dateString: string | null | undefined,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const date = parseDateOnlyLocal(dateString);
  if (!date) return '';
  return date.toLocaleDateString(locale, {
    timeZone: APP_DISPLAY_TIME_ZONE,
    ...options,
  });
}

/**
 * Date courante sous forme "YYYY-MM-DD" selon le calendrier Québec (America/Toronto).
 */
export function getLocalDateISO(now: Date = new Date()): string {
  return DateTime.fromJSDate(now).setZone(APP_DISPLAY_TIME_ZONE).toFormat('yyyy-MM-dd');
}
